import React from 'react';
import {defineMessages} from 'react-intl';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {LayoutDetailsBlock} from '@igp/shared/components/layout';
import type {BonusCodeDetailsViewModelKeys} from '@igp/shared/features/bonus-code';
import {BonusCodeDetailsViewModelKeysTypeName} from '@igp/shared/features/bonus-code';
import type {PlayerBonusColumn, PlayerBonusViewModelKeys} from '@igp/shared/features/player-bonus';
import {
    DataGridPlayerBonusServer,
    PlayerBonusColumnTypeName,
    PlayerBonusViewModelKeysTypeName,
    usePlayerBonuses,
} from '@igp/shared/features/player-bonus';
import {RealtimeMessageTrigger, RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {ViewType} from '@igp/shared/redux/view';
import {isStringNullOrEmpty} from '@igp/shared/utils';
import {EntityType} from '@models';

const localized = defineMessages({
    emptyList: {
        id: 'PlayerCashBonuses_emptyList',
        defaultMessage: 'Bonus codes list is empty',
    },
    title: {
        id: 'PlayerCashBonuses_title',
        defaultMessage: 'Cash Bonuses',
    },
});

type PlayerCashBonusesProps = {
    uid: string;
};

export function PlayerCashBonuses({uid}: PlayerCashBonusesProps) {
    const viewType: ViewType = 'CashBonuses';
    const mapper = useAutoMapper();

    let columns: PlayerBonusColumn[] = [
        'bonus_status',
        'marketing_code',
        'bonus_name',
        'desc',
        'realized_amount',
        'total_amount',
        'remaining_amount',
        'expire_at.seconds',
    ];
    columns = columns.filter((c: PlayerBonusColumn) => c);

    const playerBonusKeys: PlayerBonusViewModelKeys[] = mapper.map<PlayerBonusColumn[], PlayerBonusViewModelKeys[]>(
        columns,
        `${PlayerBonusColumnTypeName}[]`,
        `${PlayerBonusViewModelKeysTypeName}[]`
    );
    const bonusCodeKeys: BonusCodeDetailsViewModelKeys[] = mapper.map<PlayerBonusColumn[], BonusCodeDetailsViewModelKeys[]>(
        columns,
        `${PlayerBonusColumnTypeName}[]`,
        `${BonusCodeDetailsViewModelKeysTypeName}[]`
    );

    const {items, rawItems, totalCount, searchFilter, handlePageChange, handlePageSizeChange, handleSortChange} = usePlayerBonuses(
        {
            viewType,
            fields: [...playerBonusKeys, 'bonus_engine_code'],
            realtimeMode: RealtimeUpdatesMode.Confirm,
            triggers: [{type: RealtimeMessageTrigger.Add}, {type: RealtimeMessageTrigger.Update}],
            defaultFilters: [{key: 'uid', value: uid}],
            defaultSorting: [{field: 'created_at.seconds', sort: 'desc'}],
            validateFilter: filter => !isStringNullOrEmpty(filter?.find(f => f.key === 'uid')?.value),
            playerBonusEntityType: EntityType.PlayerBonus,
        },
        {
            viewType,
            fields: bonusCodeKeys,
            bonusCodeEntityType: EntityType.BonusCode,
        }
    );

    return (
        <LayoutDetailsBlock
            title={localized.title}
            content={
                <DataGridPlayerBonusServer
                    autoHeight
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    rowCount={totalCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSortModelChange={handleSortChange}
                    page={searchFilter?.paging?.page}
                    pageSize={searchFilter?.paging?.pageSize}
                    sortModel={searchFilter?.sorting}
                    emptyMessage={localized.emptyList}
                />
            }
        />
    );
}
